/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useRef, ReactElement, useMemo, Fragment } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';

import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import TextLink from '../../common/TextLink';
import PriceInput, { adjustStylePerDigit } from './PriceInput';
import OfferPriceBtnSet from './OfferPriceBtnSet';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { IsMobileViewPort, addCommas } from '../../../utils/helpers';
import {
  sendOffer,
  sendCancel,
  sendSuggest,
  sendAmountCustom,
  counterOffer,
  counterCancel,
  counterModalCustomAmount,
} from '../../../actions/profileTrackAction';
import isEmpty from 'lodash/isEmpty';
import WYPButtonIcon from '../../../atoms/icons/WYPButtonIcon';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import { PriceContainer, getInputProps, CurrencyTip, CurrencyLabelSpan, status } from './common';
import { getDefaultOfferAmount } from '../../../utils/offers';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Account, AccountMaker } from '../../../models/Account';
import { displayWarnMessage } from '../../../actions/notificationActions';
import { clearErrors } from '../../../actions/commonActions';
import { RootState } from '../../../store';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { defaultOfferAmount } from '../../../common';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';
import ModalWithPattern from '../../common/modals/ModalWithPattern';
import { mobileOverlayStyle, mobileContentStyle } from '../../common/CommonStyle';
import OfferModalFooter from './OfferModalFooter';
import breakpoints from 'theme/material/breakpoints';
import borders from 'theme/borders';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const CenterDiv = styled('div')({
  textAlign: 'center',
  padding: '20px 0',
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  color: getPatternModal().headerColor,
  [breakpoints.down(639.05)]: {
    padding: 15,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  gap: 16,
  [breakpoints.down(639.05)]: {
    gap: 10,
  },
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: '1',
  justifyContent: 'space-between',
});

const colorType = (price, hasErrors) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

const CounterRange = styled('div')({
  fontSize: 14,
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  marginTop: 20,
  marginBottom: 20,
  textAlign: 'center',
  color: getPatternModal().headerColor,
});

const ErrorContainer = styled('div')({
  textAlign: 'center',
  marginTop: '20px',
});

const ErrorMessage = styled('div')({
  color: 'rgb(204, 84, 84)',
  margin: '16px auto 0',
  maxWidth: '280px',
});

const PurchaseCredits = styled('div')({
  textAlign: 'center',
  marginTop: '12px',
  textDecoration: 'underline',
});

const ButtonDiv = styled('div')({
  margin: 0,
});

const OfferAmounts = [50, 75, 100, 150, 200];

const getHeadline = (label, currInitial, offer, currentUserIsGenerous, isCounter, username, userGender) => {
  const hasOffer =
    offer && !offer.suggest && offer.price_local && offer.price_original && offer.price_original !== null;

  const offerDescription = currentUserIsGenerous && isCounter ? 'request' : 'offer';

  if (isCounter || hasOffer) {
    const price_original = offer && offer.price_original ? offer.price_original : 0;

    return `Counter ${currInitial}${label}${price_original} ${offerDescription} from ${username}`;
  } else {
    return userGender === 'Male' ? (
      <span>
        Send <strong>{username}</strong> a first date incentive!
      </span>
    ) : (
      <span>
        Request a first date incentive from <strong>{username}</strong>!
      </span>
    );
  }
};

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.data.min_counter_price : offer && offer.data.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const renderCounterRange = (isCounter, currencyInitial, currLabel, minValue, maxValue) => {
  if (isCounter) {
    return (
      <CounterRange>
        min {currencyInitial}
        {currLabel}
        {minValue} max {currencyInitial}
        {currLabel}
        {maxValue}
      </CounterRange>
    );
  }
  return null;
};

const setCreateOfferAction = (_e: Event, offerAction: string) => {
  switch (offerAction) {
    case 'Send Counter':
      counterOffer();
      break;
    case 'Cancel Counter':
      counterCancel();
      break;
    case 'Send Offer':
      sendOffer();
      break;
    case 'Cancel Offer':
      sendCancel();
      break;
    case 'Suggest Offer':
      sendSuggest();
      break;
    default:
      return;
  }
};

const trackFocus = (ref, actionMethod) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => actionMethod(e), false);
  }

  return () => {
    ref.current.removeEventListener('click', () => null, false);
  };
};

interface FavOfferCreateProps {
  isModalOpen: boolean;
  avatarSrc: any;
  offer?: any;
  isCounter: any;
  currencyLabel: any;
  currencyInitial: any;
  currentUserIsGenerous: any;
  sex: any;
  username: any;
  sendSuggestion: any;
  cannot_make_offer_reasons: any;
  counterOffer: any;
  createNewOffer: any;
  hashId: any;
  buttonID: any;
  successOpen: any;
  isSuggestRequest: any;
  userGender?: string;
  title: string;
  pageTitle?: string;
  otherProfile: any;
  accountModel: Account;
  customContentStyle?: any;
  closeModal: () => void;
}

const FavOfferCreate = ({
  isModalOpen,
  avatarSrc,
  offer = '',
  isCounter,
  currencyLabel,
  currencyInitial,
  currentUserIsGenerous,
  sex,
  username,
  sendSuggestion,
  cannot_make_offer_reasons,
  counterOffer,
  createNewOffer,
  hashId,
  buttonID,
  successOpen,
  isSuggestRequest,
  userGender = 'Male',
  title = 'Request A Date!',
  pageTitle,
  otherProfile,
  accountModel,
  customContentStyle,
  closeModal,
}: FavOfferCreateProps): ReactElement => {
  const dispatch = useDispatch();
  const { errors } = useSelector((state: RootState) => state.common);
  const { isMobile } = useMediaBreakpoints();

  const defaultOfferPrice = useMemo(() => {
    const defaultAmount = getDefaultOfferAmount();

    if (otherProfile.account_type === 'Attractive') {
      return otherProfile?.default_offer_amount ?? defaultAmount;
    }

    return defaultAmount;
  }, [otherProfile]);

  const shouldShowTooManyPendingOffersErrorModal = useMemo(
    () => accountModel.shouldShowTooManyLockedOffersDeals(),
    [accountModel]
  );

  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState(isCounter ? '' : defaultOfferPrice);
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);
  const sendOfferBtn = useRef(null);
  const sendCancelBtn = useRef(null);
  const sendSuggestLink = useRef(null);
  const priceInput = useRef(null);
  const { setShowTooManyPendingOfferModal } = useCommonModal();

  const { showMandatoryWall } = useMandatoryWall();

  useOnClickTriggerTracking(isCounter ? 'Send Counter' : 'Send Offer', sendOfferBtn, setCreateOfferAction);
  useOnClickTriggerTracking(isCounter ? 'Cancel Counter' : 'Cancel Offer', sendCancelBtn, setCreateOfferAction);
  useOnClickTriggerTracking('Suggest Offer', sendSuggestLink, setCreateOfferAction);

  useEffect(() => {
    dispatch(clearErrors());
  }, []);

  useEffect(() => {
    trackFocus(priceInput, isCounter ? counterModalCustomAmount : sendAmountCustom);
  }, [isCounter]);

  useEffect(() => {
    setMaxValue(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
    setMinValue(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  }, [offer]);

  const updateOfferPrice = (val, error) => {
    if (!error) {
      dispatch(clearErrors());
    }
    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const inputOnChange = e => {
    const inputVal = Number(e.target.value);
    const minValueNum = Number(minValue);
    const maxValueNum = Number(maxValue);
    const maxLength = maxValue.toString().length;

    if (e.target.value.length <= maxLength) {
      const isError = inputVal < minValueNum || inputVal > maxValueNum;

      updateOfferPrice(inputVal, isError);
    } else {
      e.preventDefault();
    }
  };

  const currLabel = String.fromCharCode(currencyLabel);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = { price: offerPrice, account: hashId, source: pageTitle };
    const offerAmount = `${currencyInitial}${currLabel}${offerPrice}`;

    setOfferSending(true);
    showMandatoryWall(
      () => {
        if (isCounter || isSuggestRequest) {
          counterOffer(formData)
            .then(() => {
              closeModal();
              setOfferSending(false);
              successOpen(true, offerAmount, true);
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });
        } else {
          createNewOffer(formData)
            .then(() => {
              closeModal();
              setOfferSending(false);
              successOpen(true, offerAmount);
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });
        }
      },
      () => setOfferSending(false),
      isCounter || isSuggestRequest ? 'Counter Offer' : 'Make Offer'
    );
  };

  const onHandleSuggestion = e => {
    e.preventDefault();
    // check to see if user can make an offer before allowing it.
    setOfferSending(true);
    showMandatoryWall(
      () => {
        if (cannot_make_offer_reasons === null) {
          // process suggestion

          sendSuggestion(hashId)
            .then(() => {
              closeModal();
              setOfferSending(false);
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });
        } else {
          if (shouldShowTooManyPendingOffersErrorModal) {
            setShowTooManyPendingOfferModal(true);
          } else {
            // explain suggestion fail reason.
            dispatch(
              displayWarnMessage({
                info: `You cannot send an offer suggestion: ${cannot_make_offer_reasons[0]}`,
              })
            );
            setOfferSending(false);
          }
        }
      },
      () => setOfferSending(false),
      'Make Offer'
    );
  };

  const formatSubmitButtonText = () => {
    return isOfferSending ? `Sending ${status(currentUserIsGenerous)}...` : `Send ${status(currentUserIsGenerous)}`;
  };

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="new-offer"
      closeBtnClassName="new-offer-modal-close"
      closeBtnDataTestId="new-offer-modal-close"
      modalContentLabel="New Offer Modal"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={true}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={'100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                  border: `1px solid ${borders.light.primary}`,
                }}
              />
              <TitleDiv>{title}</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 5,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline(currLabel, currencyInitial, offer, currentUserIsGenerous, isCounter, username, userGender)}
            </Headline>
          </HeaderDiv>
          <Content>
            <React.Fragment>
              <PriceContainer
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                hasErrors={hasErrors}
              >
                <CurrencyTip
                  offerPrice={colorType(offerPrice, hasErrors)}
                  style={{
                    paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
                  }}
                >
                  <CurrencyLabelSpan
                    customCurrencyLabelStyle={{
                      fontSize: 60,
                      fontWeight: 400,
                      lineHeight: '50px',
                      letterSpacing: -0.3,
                      [breakpoints.down('sm')]: { fontSize: '50px', lineHeight: '50px' },
                    }}
                  >
                    {currencyInitial}
                    {currLabel}
                  </CurrencyLabelSpan>
                  <PriceInput
                    inputRef={priceInput}
                    data-test-id="offer-price-input"
                    hasErrors={hasErrors}
                    value={offerPrice}
                    onChange={inputOnChange}
                    validateTextFn={validateTextInput}
                    placeholder={defaultValue}
                    inputProps={{
                      style: {
                        ...getInputProps().style,
                        marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                        textAlign: 'left',
                        fontSize: 60,
                        fontWeight: 400,
                        lineHeight: '50px',
                        letterSpacing: -0.3,
                      },
                    }}
                    minWidth={isMobile ? 100 : 120}
                    responsiveFontSize={`
                      @media (max-width: 1023.95px) {
                        font-size: 50px !important;
                        line-height: 50px !important;
                      }
                   `}
                  />
                </CurrencyTip>
                {renderCounterRange(isCounter, currencyInitial, currLabel, minValue, maxValue)}
              </PriceContainer>
              <OfferPriceBtnSet
                prices={OfferAmounts}
                updateOfferPrice={updateOfferPrice}
                currentAmount={offerPrice}
                currLabel={currLabel}
                currInitial={currencyInitial}
                offer={offer}
                isCounter={isCounter}
              />
              <CounterRange data-test-id="profile-offer-desc">
                Maximum {currentUserIsGenerous ? 'Offer' : 'Request'} amount is{' '}
                {
                  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                  currencyInitial + currLabel + addCommas(parseInt(maxValue, 10))
                }
              </CounterRange>
              {!isEmpty(errors) ? (
                <ErrorContainer>
                  <ErrorMessage>{errors.price}</ErrorMessage>
                  {currentUserIsGenerous && Number(offerPrice) >= 200 && (
                    <PurchaseCredits>
                      <TextLink to="/packages">Purchase credits now.</TextLink>
                    </PurchaseCredits>
                  )}
                </ErrorContainer>
              ) : null}
            </React.Fragment>
            <ButtonDiv>
              <ButtonSet align="center">
                <Button
                  id={buttonID}
                  buttonRef={sendOfferBtn}
                  disabled={hasErrors || isOfferSending}
                  buttonType="error"
                  onClick={handleSubmit}
                  type="submit"
                  customStyle={{
                    fontSize: 18,
                    lineHeight: '16px',
                    letterSpacing: -0.3,
                    padding: '12px 28px',
                    borderRadius: '4px !important',
                  }}
                >
                  <WYPButtonIcon style={{ marginRight: 6 }} />
                  {formatSubmitButtonText()}
                </Button>
              </ButtonSet>
              {!isSuggestRequest && (
                <React.Fragment>
                  <CenterDiv>
                    <strong>&mdash; OR &mdash;</strong>
                  </CenterDiv>
                  <span>
                    <ButtonSet align="center">
                      <TextLink
                        linkRef={sendSuggestLink}
                        data-test-id="offer-suggest-link"
                        to="/"
                        disabled={hasErrors || isOfferSending}
                        onClick={e => onHandleSuggestion(e)}
                        type="submit"
                        style={{
                          color: '#E21D4A',
                          textDecoration: 'underline',
                          fontSize: isMobile ? 16 : 18,
                          lineHeight: '23px',
                          letterSpacing: -0.5,
                        }}
                      >
                        {' '}
                        Ask {sex === 'Male' ? 'him' : 'her'} to suggest an amount
                      </TextLink>
                    </ButtonSet>
                  </span>
                </React.Fragment>
              )}
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={sendCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  disabled={isOfferSending}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                  }}
                  customStyle={{
                    color: borders.light.secondary,
                    fontWeight: 'normal',
                    textDecoration: 'none',
                    padding: '20px 0',
                  }}
                >
                  Cancel
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
      </Fragment>
      <OfferModalFooter />
    </ModalWithPattern>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountModel: AccountMaker.create(state.profile),
  };
};

export default connect(mapStateToProps, {})(FavOfferCreate);
